/**
 * PolizzaCommercioPage — FASE 10C.3
 *
 * Wizard pubblico 5 step "Polizza Commercio" per esercizi commerciali.
 * Mobile-first, framer-motion, persistenza localStorage, honeypot anti-bot.
 *
 * NB: nessun valore € viene mai mostrato lato cliente — il calcolo silente
 * di valore di ricostruzione è gestito server-side e visibile SOLO in admin.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Store, MapPin, Briefcase, Shield, User, Loader2, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import IVASSComplianceCheckboxes, { buildMupVisioneMeta, isIvassComplianceOk } from "@/components/IVASSComplianceCheckboxes";
import ConsensiGranulari, { buildConsensiGranulariMeta, isConsensiOk } from "@/components/ConsensiGranulari";
import PrivacyFieldTooltip from "@/components/PrivacyFieldTooltip";
import ScudoProfessionaleStep, { SCUDO_PROFESSIONI, initialScudoState, isScudoValid, buildScudoPayload } from "@/components/ScudoProfessionaleStep";
import ContattoPreferenzeBlock from "@/components/ContattoPreferenzeBlock";
import AcceptAllInformative from "@/components/AcceptAllInformative";
import SettoriMerceologiciSelect from "@/components/SettoriMerceologiciSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// v1.71.2 — bump chiave draft: schema Scudo semplificato incompatibile con v1.
const STORAGE_KEY = "polizza_commercio_draft_v2";
const OLD_STORAGE_KEY_V1 = "polizza_commercio_draft_v1";

const TIPI_ATTIVITA = [
  { value: "negozio_retail", label: "Negozio retail", desc: "Abbigliamento, alimentari, articoli, ferramenta…" },
  { value: "ristorante_bar", label: "Ristorante / Bar", desc: "Ristorazione, pizzeria, bar, gelateria, pub…" },
  { value: "servizi_persona", label: "Servizi alla persona", desc: "Parrucchiere, estetista, centro benessere…" },
  { value: "attivita_professionale", label: "Attività professionale", desc: "Studio professionale, ufficio, consulenza…" },
  { value: "altro", label: "Altro", desc: "Altre attività commerciali" },
];

const FATTURATO_OPTS = [
  { value: "lt_50k", label: "< 50.000 €" },
  { value: "50k_200k", label: "50.000 – 200.000 €" },
  { value: "200k_500k", label: "200.000 – 500.000 €" },
  { value: "500k_2m", label: "500.000 – 2.000.000 €" },
  { value: "gt_2m", label: "> 2.000.000 €" },
];

const COPERTURE_LIST = [
  { key: "incendio", label: "Incendio e scoppio", regoleKey: "incendio" },
  { key: "furto", label: "Furto e rapina", regoleKey: "furto" },
  { key: "rc_terzi", label: "RC verso terzi", regoleKey: "rct" },
  { key: "perdita_pecuniaria", label: "Perdita pecuniaria", regoleKey: null },
  { key: "cyber", label: "Cyber risk", regoleKey: null },
  { key: "infortuni_dipendenti", label: "Infortuni dipendenti", regoleKey: null },
];

function StepBadge({ active, done, num, icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
      <div
        className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-all ${
          done
            ? "bg-emerald-600 border-emerald-600 text-white"
            : active
            ? "bg-[#163321] border-[#163321] text-white"
            : "bg-white border-[#E5E2DB] text-[#4A5E51]"
        }`}
      >
        {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      </div>
      <span className={`text-[10px] uppercase tracking-wide font-semibold ${active ? "text-[#163321]" : "text-[#4A5E51]"} text-center hidden sm:block`}>
        {label}
      </span>
    </div>
  );
}

export default function PolizzaCommercioPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // v1.56 (feb-2026) — Click-wrap IVASS: art. 1892 c.c. + MUP Reg. 40/2018
  const [signedDeclaration, setSignedDeclaration] = useState(false);
  const [mupSeen, setMupSeen] = useState(false);
  const [mupFilename, setMupFilename] = useState(null);
  // v1.66 (Fase 2 Privacy) — Consensi granulari GDPR
  const [consensi, setConsensi] = useState({});
  // v1.72.0 — Preferenze ricontatto (canale + fascia oraria)
  const [preferenzeRicontatto, setPreferenzeRicontatto] = useState({ canale: "", fascia: "" });
  // v1.72.2 — Regole di compatibilità settore (fetch al cambio settore)
  const [regoleSettore, setRegoleSettore] = useState(null);
  const [regoleLoading, setRegoleLoading] = useState(false);
  // v1.72.2 — Requisiti hard-required (es. videosorveglianza distributori)
  const [requisitiSettore, setRequisitiSettore] = useState({});
  // v1.72.2 — Scelta utente per Tatuatori: procede con Scudo o solo commerciale?
  const [tatuatoreVuoleScudo, setTatuatoreVuoleScudo] = useState(null); // null | true | false

  // v1.72.2 — Fetch regole quando il settore cambia (debounce implicito via useEffect)
  useEffect(() => {
    const cod = data.attivita.settore;
    if (!cod) { setRegoleSettore(null); setRequisitiSettore({}); setTatuatoreVuoleScudo(null); return; }
    let cancelled = false;
    setRegoleLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/commercio/settori/${encodeURIComponent(cod)}/regole`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setRegoleSettore(data);
      } catch { if (!cancelled) setRegoleSettore(null); }
      finally { if (!cancelled) setRegoleLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [data.attivita.settore]);

  // v1.72.0 — Helper "Accetta tutto" per informative facoltative
  const handleAcceptAllInformative = () => {
    setSignedDeclaration(true);
    setMupSeen(true);
    setConsensi((prev) => ({
      ...prev,
      marketing: true,
      profilazione: true,
      gruppo_partner: true,
    }));
  };
  const sessionHashCOMM = useMemo(
    () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, []);

  const [data, setData] = useState(() => {
    // v1.71.2 — schema Scudo semplificato. Non caricare draft v1 (incompatibile),
    // ma puliscilo silenziosamente per non lasciare byte orfani in localStorage.
    try {
      localStorage.removeItem(OLD_STORAGE_KEY_V1);
    } catch { /* ignore */ }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Difensivo: garantisce che il draft abbia la shape v2 attesa
        if (!parsed.scudo_professionale) parsed.scudo_professionale = initialScudoState();
        if (parsed.attivita && parsed.attivita.settore === undefined) parsed.attivita.settore = "";
        if (parsed.attivita && parsed.attivita.macro_categoria === undefined) parsed.attivita.macro_categoria = "";
        if (parsed.attivita && !Array.isArray(parsed.attivita.professioni_esercitate)) {
          parsed.attivita.professioni_esercitate = [];
        }
        return parsed;
      }
    } catch { /* ignore */ }
    return {
      attivita: { tipo: "", descrizione: "", settore: "", macro_categoria: "", professioni_esercitate: [] },
      sede: { indirizzo: "", citta: "", provincia: "", cap: "", superficie_mq: "", num_locali: "", ha_vetrine: false, ha_insegne: false },
      caratteristiche: { anno_apertura: "", num_dipendenti: "", fatturato_range: "", valore_merce_eur: "" },
      coperture: { incendio: false, furto: false, rc_terzi: false, perdita_pecuniaria: false, cyber: false, infortuni_dipendenti: false, altro: "" },
      contatto: { nome_titolare: "", ragione_sociale: "", partita_iva: "", codice_fiscale: "", email: "", telefono: "", consenso_privacy: false, consenso_marketing: false },
      scudo_professionale: initialScudoState(),
      _hp: "",
    };
  });

  // Persist su localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }, [data]);

  const merge = (path, value) => {
    setData((d) => {
      const ks = path.split(".");
      const next = { ...d };
      let cur = next;
      for (let i = 0; i < ks.length - 1; i++) {
        cur[ks[i]] = { ...(cur[ks[i]] || {}) };
        cur = cur[ks[i]];
      }
      cur[ks[ks.length - 1]] = value;
      return next;
    });
  };

  // v1.71.1 — Step dinamici: quando settore=operatore_benessere OR tatuatori
  // (con scelta positiva) → step aggiuntivo "Scudo Professionale".
  const hasScudo = (
    (data.attivita.tipo === "servizi_persona" && data.attivita.settore === "operatore_benessere")
    || (data.attivita.settore === "tatuatori" && tatuatoreVuoleScudo === true)
  );

  const STEPS = useMemo(() => {
    const base = [
      { key: "attivita", icon: Store, label: "Attività" },
      { key: "sede", icon: MapPin, label: "Sede" },
      { key: "caratteristiche", icon: Briefcase, label: "Caratteristiche" },
      { key: "coperture", icon: Shield, label: "Coperture" },
    ];
    if (hasScudo) base.push({ key: "scudo", icon: HeartHandshake, label: "Scudo Prof." });
    base.push({ key: "contatto", icon: User, label: "Contatti" });
    return base;
  }, [hasScudo]);

  // Clamp step index quando la sequenza cambia (aggiunta/rimozione step scudo)
  useEffect(() => {
    if (step > STEPS.length - 1) setStep(STEPS.length - 1);
  }, [STEPS.length, step]);

  const currentKey = STEPS[step]?.key;

  const canNext = useMemo(() => {
    if (currentKey === "attivita") {
      // v1.72.1 — Settore obbligatorio per TUTTI i tipi (dropdown 2-livelli).
      if (!data.attivita.tipo) return false;
      if (!data.attivita.settore) return false;
      // Se settore=operatore_benessere, obbligo min 1 professione.
      if (data.attivita.settore === "operatore_benessere") {
        const list = data.attivita.professioni_esercitate || [];
        if (list.length === 0) return false;
      }
      return true;
    }
    if (currentKey === "sede") {
      const s = data.sede;
      return Boolean(s.indirizzo && s.citta && /^[A-Za-z]{2}$/.test(s.provincia) && /^\d{5}$/.test(s.cap) && Number(s.superficie_mq) > 0);
    }
    if (currentKey === "caratteristiche") return true;
    if (currentKey === "coperture") {
      const atLeastOne = Object.entries(data.coperture).some(([k, v]) => k !== "altro" && v === true);
      if (!atLeastOne) return false;
      // v1.72.2 — Requisiti obbligatori del settore (es. videosorveglianza distributori)
      const req = regoleSettore?.requisiti_obbligatori || [];
      if (req.some((r) => !requisitiSettore[r.codice])) return false;
      // v1.72.2 — Se tatuatori, richiedi scelta esplicita Scudo SI/NO
      if (data.attivita.settore === "tatuatori" && tatuatoreVuoleScudo === null) return false;
      return true;
    }
    if (currentKey === "scudo") return isScudoValid(data.scudo_professionale);
    if (currentKey === "contatto") {
      const c = data.contatto;
      return Boolean(c.nome_titolare && c.email && c.telefono && c.consenso_privacy && (c.partita_iva || c.codice_fiscale) && isIvassComplianceOk({ signedDeclaration, mupSeen }) && isConsensiOk(consensi));
    }
    return false;
  }, [currentKey, data, signedDeclaration, mupSeen, consensi, regoleSettore, requisitiSettore, tatuatoreVuoleScudo]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        attivita: {
          tipo: data.attivita.tipo,
          descrizione: data.attivita.descrizione,
          // v1.72.1 — invio settore + macro coerenti dal <SettoriMerceologiciSelect>
          ...(data.attivita.settore ? { settore: data.attivita.settore } : {}),
          ...(data.attivita.macro_categoria ? { macro_categoria: data.attivita.macro_categoria } : {}),
          // v1.71.2 — Professioni esercitate spostate dal Scudo (min 1 se operatore_benessere)
          ...(hasScudo && Array.isArray(data.attivita.professioni_esercitate) && data.attivita.professioni_esercitate.length > 0
            ? { professioni_esercitate: data.attivita.professioni_esercitate }
            : {}),
        },
        sede: {
          ...data.sede,
          superficie_mq: Number(data.sede.superficie_mq),
          num_locali: data.sede.num_locali ? Number(data.sede.num_locali) : null,
        },
        caratteristiche: {
          anno_apertura: data.caratteristiche.anno_apertura ? Number(data.caratteristiche.anno_apertura) : null,
          num_dipendenti: data.caratteristiche.num_dipendenti ? Number(data.caratteristiche.num_dipendenti) : null,
          fatturato_range: data.caratteristiche.fatturato_range || null,
          valore_merce_eur: data.caratteristiche.valore_merce_eur === "" || data.caratteristiche.valore_merce_eur === null || data.caratteristiche.valore_merce_eur === undefined
            ? null
            : Number(data.caratteristiche.valore_merce_eur),
        },
        coperture: data.coperture,
        // v1.72.2 — Requisiti hard-required del settore (es. videosorveglianza)
        ...(Object.keys(requisitiSettore).length > 0 ? { requisiti_settore: requisitiSettore } : {}),
        contatto: {
          ...data.contatto,
          // v1.72.0 — Preferenze ricontatto (invia solo se compilato)
          ...((preferenzeRicontatto.canale || preferenzeRicontatto.fascia)
            ? { preferenze_ricontatto: {
                canale: preferenzeRicontatto.canale || null,
                fascia: preferenzeRicontatto.fascia || null,
              } }
            : {}),
        },
        // v1.71.1 — Scudo Professionale (Operatore del Benessere)
        ...(hasScudo ? { scudo_professionale: buildScudoPayload(data.scudo_professionale) } : {}),
        _hp: data._hp,
        // v1.56 — Click-wrap IVASS
        signed_declaration: !!signedDeclaration,
        session_hash: sessionHashCOMM,
        mup_visione: buildMupVisioneMeta({ mupSeen, mupFilename }),
        consensi_granulari: buildConsensiGranulariMeta(consensi),
      };
      const { data: r } = await axios.post(`${API}/polizza-commercio/submit`, payload);
      // Anti-leak: nessun valore € letto/mostrato nemmeno se per errore arrivasse
      toast.success("Richiesta inviata correttamente");
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      navigate(`/conferma?ref=${r.request_id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "Errore invio richiesta";
      toast.error(typeof detail === "string" ? detail : "Verifica i dati inseriti");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F4F0] to-[#FAF9F5] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl text-[#163321]">Polizza Commercio</h1>
          <p className="text-sm text-[#4A5E51] mt-1">Proteggi la tua attività commerciale in 5 semplici passaggi.</p>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-3 sm:p-5 mb-4">
          <div className="flex items-center gap-1 sm:gap-2">
            {STEPS.map((s, i) => (
              <div key={i} className="flex-1 flex items-center">
                <StepBadge active={step === i} done={i < step} num={i + 1} icon={s.icon} label={s.label} />
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${i < step ? "bg-emerald-600" : "bg-[#E5E2DB]"}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-5 sm:p-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.18 }}
            >
              {currentKey === "attivita" && <StepAttivita data={data} merge={merge} />}
              {currentKey === "sede" && <StepSede data={data} merge={merge} />}
              {currentKey === "caratteristiche" && <StepCaratteristiche data={data} merge={merge} />}
              {currentKey === "coperture" && (
                <StepCoperture
                  data={data}
                  merge={merge}
                  regole={regoleSettore}
                  regoleLoading={regoleLoading}
                  requisiti={requisitiSettore}
                  onRequisitiChange={setRequisitiSettore}
                  tatuatoreVuoleScudo={tatuatoreVuoleScudo}
                  onTatuatoreScudoChange={setTatuatoreVuoleScudo}
                />
              )}
              {currentKey === "scudo" && (
                <ScudoProfessionaleStep
                  value={data.scudo_professionale}
                  onChange={(next) => setData((d) => ({ ...d, scudo_professionale: next }))}
                  professioniSelezionate={data.attivita.professioni_esercitate || []}
                  onEditProfessioni={() => setStep(0)}
                />
              )}
              {currentKey === "contatto" && (
                <>
                  <StepContatto data={data} merge={merge} />
                  {/* v1.72.0 — Preferenze ricontatto (canale + fascia oraria) */}
                  <div className="mt-5 pt-5 border-t border-[#E5E2DB]">
                    <p className="text-xs uppercase tracking-wide text-[#4A5E51] mb-3">Come preferisci essere ricontattato/a?</p>
                    <ContattoPreferenzeBlock
                      value={preferenzeRicontatto}
                      onChange={setPreferenzeRicontatto}
                      testidPrefix="commercio-ricontatto"
                    />
                  </div>
                  {/* v1.72.0 — Accetta tutte le informative facoltative in blocco */}
                  <div className="mt-5">
                    <AcceptAllInformative
                      onAcceptAll={handleAcceptAllInformative}
                      testid="commercio-accept-all-informative"
                    />
                  </div>
                  {/* v1.56 (feb-2026) — Doppia checkbox IVASS pre-submit */}
                  <div className="mt-5 pt-5 border-t border-[#E5E2DB]">
                    <IVASSComplianceCheckboxes
                      signedDeclaration={signedDeclaration}
                      onSignedDeclarationChange={setSignedDeclaration}
                      mupSeen={mupSeen}
                      onMupSeenChange={setMupSeen}
                      mupFilename={mupFilename}
                      onMupFilenameChange={setMupFilename}
                      testIdPrefix="commercio"
                    />
                  </div>
                  {/* v1.66 — Consensi granulari GDPR */}
                  <div className="mt-4">
                    <ConsensiGranulari
                      value={consensi}
                      onChange={setConsensi}
                      testIdPrefix="commercio"
                      includeSanitario={false}
                    />
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Honeypot — nascosto a tab order e screen reader */}
          <input
            type="text"
            value={data._hp}
            onChange={(e) => merge("_hp", e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
          />

          {/* Nav */}
          <div className="flex items-center justify-between mt-7 pt-5 border-t border-[#E5E2DB]">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0 || submitting}
              className="rounded-full"
              data-testid="commercio-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />Indietro
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext}
                className="rounded-full bg-[#163321] hover:bg-[#0F1F14] text-white"
                data-testid="commercio-next"
              >
                Avanti<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={!canNext || submitting}
                className="rounded-full bg-[#D97B58] hover:bg-[#C06A48] text-white"
                data-testid="commercio-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Invia richiesta
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-[#4A5E51] mt-4">
          I tuoi dati sono trattati nel rispetto del GDPR — riceverai contatto entro 24h lavorative.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Step components
// ============================================================================
function StepAttivita({ data, merge }) {
  return (
    <div>
      <h2 className="font-heading text-xl text-[#163321] mb-4">Che tipo di attività gestisci?</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {TIPI_ATTIVITA.map((t) => (
          <label
            key={t.value}
            className={`p-4 rounded-xl border cursor-pointer transition-all ${
              data.attivita.tipo === t.value ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] hover:bg-[#F5F4F0]"
            }`}
            data-testid={`tipo-${t.value}`}
          >
            <input
              type="radio"
              name="tipo_attivita"
              checked={data.attivita.tipo === t.value}
              onChange={() => {
                merge("attivita.tipo", t.value);
                // Reset settore/macro/professioni se cambia tipo (evita orfani)
                if (t.value !== "servizi_persona") {
                  merge("attivita.professioni_esercitate", []);
                }
                // v1.72.1 — reset macro+settore su cambio tipo per coerenza dropdown
                merge("attivita.macro_categoria", "");
                merge("attivita.settore", "");
              }}
              className="sr-only"
            />
            <div className="font-semibold text-sm text-[#0F1F14]">{t.label}</div>
            <div className="text-xs text-[#4A5E51] mt-0.5">{t.desc}</div>
          </label>
        ))}
      </div>

      {/* v1.72.1 — Dropdown 2-livelli (macro → settore) fetch da /api/commercio/settori */}
      <div className="mt-4">
        <p className="text-xs uppercase tracking-wide text-[#4A5E51] mb-2">Classifica la tua attività *</p>
        <SettoriMerceologiciSelect
          value={{ macro: data.attivita.macro_categoria, settore: data.attivita.settore }}
          onChange={(next) => {
            merge("attivita.macro_categoria", next.macro || "");
            merge("attivita.settore", next.settore || "");
            // Reset professioni se il settore non è operatore_benessere
            if (next.settore !== "operatore_benessere") {
              merge("attivita.professioni_esercitate", []);
            }
          }}
          testidPrefix="attivita"
        />

        {data.attivita.settore === "operatore_benessere" && (
          <div className="mt-3 bg-[#F5F4F0] border border-[#E5E2DB] rounded-lg p-3">
            <p className="text-[11px] text-[#4A5E51] mb-3">
              Seleziona una o più <strong>professioni esercitate</strong>. Aggiungeremo uno step dedicato con il
              modulo di proposta RC <strong>Scudo Professionale — Operatore del Benessere</strong>.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SCUDO_PROFESSIONI.map((p) => {
                const list = data.attivita.professioni_esercitate || [];
                const checked = list.includes(p.code);
                return (
                  <label
                    key={p.code}
                    className={`text-[12px] px-3 py-2 rounded-xl border cursor-pointer flex items-center gap-2 transition-all bg-white ${
                      checked ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] hover:bg-[#F5F4F0]"
                    }`}
                    data-testid={`attivita-prof-${p.code}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const cur = data.attivita.professioni_esercitate || [];
                        const next = cur.includes(p.code) ? cur.filter((x) => x !== p.code) : [...cur, p.code];
                        merge("attivita.professioni_esercitate", next);
                      }}
                      className="accent-[#163321]"
                    />
                    <span>{p.label}</span>
                      </label>
                    );
                  })}
                </div>
                {(data.attivita.professioni_esercitate || []).length === 0 && (
                  <p className="text-[11px] text-[#D97B58] mt-2" data-testid="attivita-prof-error">
                    Seleziona almeno una professione per proseguire.
                  </p>
                )}
              </div>
        )}
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Descrizione (opzionale)</label>
        <textarea
          value={data.attivita.descrizione}
          onChange={(e) => merge("attivita.descrizione", e.target.value)}
          placeholder="Es. Abbigliamento sportivo, ristorazione tipica, …"
          className="w-full mt-1 p-3 border border-[#E5E2DB] rounded-xl text-sm min-h-[60px]"
          maxLength={500}
        />
      </div>
    </div>
  );
}

function StepSede({ data, merge }) {
  const s = data.sede;
  return (
    <div>
      <h2 className="font-heading text-xl text-[#163321] mb-4">Sede commerciale</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Indirizzo *</label>
          <input value={s.indirizzo} onChange={(e) => merge("sede.indirizzo", e.target.value)} placeholder="Via/Piazza, civico" className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" data-testid="sede-indirizzo" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Città *</label>
          <input value={s.citta} onChange={(e) => merge("sede.citta", e.target.value)} placeholder="Es. Roma" className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" data-testid="sede-citta" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Prov. *</label>
            <input value={s.provincia} onChange={(e) => merge("sede.provincia", e.target.value.toUpperCase().slice(0, 2))} placeholder="RM" className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm uppercase" data-testid="sede-prov" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-[#4A5E51]">CAP *</label>
            <input value={s.cap} onChange={(e) => merge("sede.cap", e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="00100" className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" data-testid="sede-cap" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Superficie utile (mq) *</label>
          <input type="number" min="1" max="100000" value={s.superficie_mq} onChange={(e) => merge("sede.superficie_mq", e.target.value)} placeholder="Es. 80" className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" data-testid="sede-mq" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Numero locali</label>
          <input type="number" min="1" max="100" value={s.num_locali} onChange={(e) => merge("sede.num_locali", e.target.value)} placeholder="2" className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" />
        </div>
        <div className="sm:col-span-2 flex flex-wrap gap-3 mt-1">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={s.ha_vetrine} onChange={(e) => merge("sede.ha_vetrine", e.target.checked)} className="accent-[#163321]" /> Vetrine esposte su strada
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={s.ha_insegne} onChange={(e) => merge("sede.ha_insegne", e.target.checked)} className="accent-[#163321]" /> Insegne luminose esterne
          </label>
        </div>
      </div>
    </div>
  );
}

function StepCaratteristiche({ data, merge }) {
  const c = data.caratteristiche;
  return (
    <div>
      <h2 className="font-heading text-xl text-[#163321] mb-4">Caratteristiche dell'attività</h2>
      <p className="text-xs text-[#4A5E51] mb-4">Tutti i campi sono opzionali — più informazioni ci dai, più precisa sarà la quotazione.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Anno apertura</label>
          <input type="number" min="1800" max="2100" value={c.anno_apertura} onChange={(e) => merge("caratteristiche.anno_apertura", e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Numero dipendenti</label>
          <input type="number" min="0" max="10000" value={c.num_dipendenti} onChange={(e) => merge("caratteristiche.num_dipendenti", e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Fatturato annuo</label>
          <select value={c.fatturato_range} onChange={(e) => merge("caratteristiche.fatturato_range", e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm bg-white">
            <option value="">Seleziona…</option>
            {FATTURATO_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Valore merce in giacenza (€)</label>
          <input
            type="number"
            min="0"
            step="100"
            inputMode="numeric"
            placeholder="Es. 15000"
            value={c.valore_merce_eur ?? ""}
            onChange={(e) => merge("caratteristiche.valore_merce_eur", e.target.value === "" ? "" : Number(e.target.value))}
            data-testid="commercio-valore-merce-eur"
            className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm bg-white"
          />
          <p className="text-[10.5px] text-[#4A5E51] mt-1">Cifra precisa in euro — verrà usata per determinare il massimale garanzie merci.</p>
        </div>
      </div>
    </div>
  );
}

function StepCoperture({ data, merge, regole, regoleLoading, requisiti, onRequisitiChange, tatuatoreVuoleScudo, onTatuatoreScudoChange }) {
  const permesse = regole?.coperture_permesse || {};
  const reqObbligatori = regole?.requisiti_obbligatori || [];
  const noteUtente = regole?.note_utente || "";
  const routing = regole?.routing || "commercio_standard";
  const isTatuatore = data.attivita.settore === "tatuatori";

  return (
    <div>
      <h2 className="font-heading text-xl text-[#163321] mb-2">Coperture richieste</h2>
      <p className="text-xs text-[#4A5E51] mb-4">Seleziona almeno una garanzia. Il consulente costruirà il pacchetto su misura.</p>

      {regoleLoading && (
        <p className="text-[11px] text-[#4A5E51] mb-3" data-testid="coperture-loading">
          Verifico compatibilità coperture per il settore selezionato…
        </p>
      )}

      {/* v1.72.2 — Alert Tatuatore: proponi RC Professionale dedicata (Scudo, branding-safe) */}
      {isTatuatore && routing === "scudo_professionale" && (
        <div className="mb-4 px-4 py-3 rounded-2xl border border-[#163321]/20 bg-[#163321]/5" data-testid="alert-tatuatore-scudo">
          <p className="text-[13px] text-[#0F1F14] mb-3">
            Per la tua attività ti proponiamo la polizza <strong>RC Professionale dedicata</strong> (Scudo Professionale),
            in aggiunta o in alternativa alla polizza commerciale. Come vuoi procedere?
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onTatuatoreScudoChange(true)}
              className={`text-sm px-3 py-2 rounded-xl border transition-all ${
                tatuatoreVuoleScudo === true
                  ? "border-[#163321] bg-[#163321] text-white font-semibold"
                  : "border-[#163321]/30 bg-white text-[#163321] hover:bg-[#163321]/10"
              }`}
              data-testid="btn-tatuatore-scudo-si"
            >
              Sì, procedo con RC Professionale
            </button>
            <button
              type="button"
              onClick={() => onTatuatoreScudoChange(false)}
              className={`text-sm px-3 py-2 rounded-xl border transition-all ${
                tatuatoreVuoleScudo === false
                  ? "border-[#163321] bg-[#163321] text-white font-semibold"
                  : "border-[#163321]/30 bg-white text-[#163321] hover:bg-[#163321]/10"
              }`}
              data-testid="btn-tatuatore-scudo-no"
            >
              No, solo copertura commerciale
            </button>
          </div>
          {tatuatoreVuoleScudo === false && (
            <p className="text-[11px] text-[#8B6F2C] mt-2">
              Nota: la RC verso terzi professionale non sarà inclusa nel preventivo commerciale.
            </p>
          )}
        </div>
      )}

      {/* v1.72.2 — Alert routing consulenza manuale (regime_speciale) */}
      {routing === "consulenza_manuale" && (
        <div className="mb-4 px-3 py-2.5 rounded-xl border border-[#C9A96E]/40 bg-[#F5EFDD] text-[12px] text-[#5C4A1F]"
             data-testid="coperture-alert-consulenza">
          <strong>Valutazione manuale necessaria.</strong> Il settore selezionato richiede una quotazione dedicata:
          un consulente ti contatterà per completare l'analisi del rischio.
          {noteUtente && <div className="mt-1 opacity-90">{noteUtente}</div>}
        </div>
      )}

      {/* v1.72.2 — Note utente verbatim (vincoli sottoscrittivi Tariffa) */}
      {routing !== "consulenza_manuale" && !isTatuatore && noteUtente && (
        <div className="mb-4 px-3 py-2.5 rounded-xl border border-[#E5E2DB] bg-[#F5F4F0] text-[12px] text-[#4A5E51]"
             data-testid="coperture-note-utente">
          <strong>Nota per il tuo settore:</strong> {noteUtente}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {COPERTURE_LIST.map((c) => {
          const bloccata = c.regoleKey && permesse[c.regoleKey] === false;
          const checked = !!data.coperture[c.key];
          return (
            <label
              key={c.key}
              title={bloccata ? "Non disponibile per il settore selezionato con la Tariffa Commercio" : undefined}
              className={`p-3 rounded-xl border flex items-center gap-2 transition-all ${
                bloccata
                  ? "border-[#E5E2DB] bg-[#F5F4F0] opacity-55 cursor-not-allowed"
                  : (checked
                      ? "border-[#163321] bg-[#163321]/5 cursor-pointer"
                      : "border-[#E5E2DB] cursor-pointer hover:bg-[#F5F4F0]")
              }`}
              data-testid={`copertura-${c.key}${bloccata ? "-blocked" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked && !bloccata}
                disabled={bloccata}
                onChange={(e) => merge(`coperture.${c.key}`, e.target.checked)}
                className="accent-[#163321]"
              />
              <span className="text-sm">
                {c.label}
                {bloccata && <span className="ml-1 text-[10px] uppercase tracking-wide text-[#8B6F2C]">· non disponibile</span>}
              </span>
            </label>
          );
        })}
      </div>

      {/* v1.72.2 — Requisiti hard-required (es. videosorveglianza) */}
      {reqObbligatori.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#E5E2DB]" data-testid="coperture-requisiti-block">
          <p className="text-xs uppercase tracking-wide text-[#4A5E51] mb-2">Requisiti richiesti per il tuo settore</p>
          <div className="space-y-2">
            {reqObbligatori.map((r) => (
              <label
                key={r.codice}
                className="flex items-start gap-2 text-sm text-[#0F1F14]"
                data-testid={`req-${r.codice}`}
              >
                <input
                  type="checkbox"
                  checked={!!requisiti[r.codice]}
                  onChange={(e) => onRequisitiChange({ ...requisiti, [r.codice]: e.target.checked })}
                  className="mt-0.5 accent-[#163321]"
                />
                <span>
                  <strong>{r.label}</strong>
                  <span className="block text-[11px] text-[#4A5E51]">
                    Requisito contrattuale della Tariffa Commercio per questo settore.
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3">
        <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Altre coperture (opzionale)</label>
        <input value={data.coperture.altro} onChange={(e) => merge("coperture.altro", e.target.value)} placeholder="Es. eventi atmosferici, danni elettrici…" className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" maxLength={300} />
      </div>
    </div>
  );
}

function StepContatto({ data, merge }) {
  const c = data.contatto;
  return (
    <div>
      <h2 className="font-heading text-xl text-[#163321] mb-4">I tuoi contatti</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Nome titolare *</label>
          <input value={c.nome_titolare} onChange={(e) => merge("contatto.nome_titolare", e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" data-testid="contatto-nome" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Ragione sociale</label>
          <input value={c.ragione_sociale} onChange={(e) => merge("contatto.ragione_sociale", e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" placeholder="Opzionale per ditta individuale" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Partita IVA</label>
          <input value={c.partita_iva} onChange={(e) => merge("contatto.partita_iva", e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" placeholder="11 cifre" data-testid="contatto-piva" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Codice Fiscale</label>
          <input value={c.codice_fiscale} onChange={(e) => merge("contatto.codice_fiscale", e.target.value.toUpperCase())} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" placeholder="Persona fisica / ditta indiv." />
        </div>
        <p className="sm:col-span-2 text-[11px] text-[#4A5E51]">* È necessario indicare almeno una tra Partita IVA e Codice Fiscale.</p>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Email *<PrivacyFieldTooltip finalita="email" testIdPrefix="commercio-email" /></label>
          <input type="email" value={c.email} onChange={(e) => merge("contatto.email", e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" data-testid="contatto-email" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-[#4A5E51]">Telefono *<PrivacyFieldTooltip finalita="telefono" testIdPrefix="commercio-telefono" /></label>
          <input value={c.telefono} onChange={(e) => merge("contatto.telefono", e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm" data-testid="contatto-telefono" />
        </div>
      </div>
      <div className="mt-5 space-y-2 text-[12px] text-[#0F1F14]">
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={c.consenso_privacy} onChange={(e) => merge("contatto.consenso_privacy", e.target.checked)} className="mt-0.5 accent-[#163321]" data-testid="contatto-privacy" />
          <span>Ho letto la <a href="/privacy" target="_blank" rel="noreferrer" className="underline">Privacy Policy</a> e acconsento al trattamento dei dati per la finalità di preventivo (obbligatorio).</span>
        </label>
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={c.consenso_marketing} onChange={(e) => merge("contatto.consenso_marketing", e.target.checked)} className="mt-0.5 accent-[#163321]" />
          <span>Acconsento a ricevere comunicazioni commerciali su offerte e iniziative (facoltativo).</span>
        </label>
      </div>
    </div>
  );
}
